import { TelemetryCurrentResourceDTO } from '@webip/shared/src/dto/telemetry';

export const telemetryStatusMeta: Record<TelemetryCurrentResourceDTO['status'], { label: string; color: string; background: string }> = {
    online: {
        label: 'Online',
        color: 'var(--status-online)',
        background: 'var(--status-online-bg)',
    },
    degraded: {
        label: 'Degradado',
        color: 'var(--status-warn)',
        background: 'var(--status-warn-bg)',
    },
    offline: {
        label: 'Offline',
        color: 'var(--status-offline)',
        background: 'var(--status-offline-bg)',
    },
    unknown: {
        label: 'Sem coleta',
        color: 'var(--status-idle)',
        background: 'var(--status-idle-bg)',
    },
};

export type TelemetryTone = {
    color: string;
    background: string;
};

const telemetryNeutralTone: TelemetryTone = {
    color: 'var(--telemetry-usage-neutral-color)',
    background: 'transparent',
};

export function getTelemetryTone(percent?: number | null): TelemetryTone {
    if (percent == null || Number.isNaN(percent) || percent < 60) {
        return telemetryNeutralTone;
    }

    if (percent < 75) {
        return {
            color: 'var(--telemetry-usage-elevated-color)',
            background: 'var(--telemetry-usage-elevated-bg)',
        };
    }

    if (percent < 90) {
        return {
            color: 'var(--telemetry-usage-high-color)',
            background: 'var(--telemetry-usage-high-bg)',
        };
    }

    return {
        color: 'var(--telemetry-usage-critical-color)',
        background: 'var(--telemetry-usage-critical-bg)',
    };
}

export function getUsagePercent(used?: number | null, total?: number | null) {
    if (used == null || total == null || Number.isNaN(used) || Number.isNaN(total) || total <= 0) return null;
    return Math.round((used / total) * 100);
}

export function formatPercent(value?: number | null) {
    if (value == null || Number.isNaN(value)) return 'n/d';
    return `${Math.round(value)}%`;
}

export function formatBytes(value?: number | null) {
    if (value == null || Number.isNaN(value)) return 'n/d';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let current = value;
    let unitIndex = 0;

    while (current >= 1024 && unitIndex < units.length - 1) {
        current /= 1024;
        unitIndex += 1;
    }

    const decimals = current >= 100 || unitIndex === 0 ? 0 : 1;
    return `${current.toFixed(decimals)} ${units[unitIndex]}`;
}

export function formatUsage(used?: number | null, total?: number | null) {
    if (used == null || total == null || Number.isNaN(used) || Number.isNaN(total) || total <= 0) return 'n/d';
    return `${formatBytes(used)} / ${formatBytes(total)} (${Math.round((used / total) * 100)}%)`;
}

export function formatUsagePercent(used?: number | null, total?: number | null) {
    const percent = getUsagePercent(used, total);
    if (percent == null) return 'n/d';
    return `${percent}%`;
}

export function formatUptime(seconds?: number | null) {
    if (seconds == null || Number.isNaN(seconds)) return 'n/d';

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

export function formatCollectedAt(value?: string | null) {
    if (!value) return 'Sem coleta';
    return new Date(value).toLocaleString('pt-BR');
}

export function formatCollectedAtShort(value?: string | null) {
    if (!value) return 'Sem coleta';
    return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}
